import React from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Paper,
  rem,
  Badge,
  Grid,
  List,
  ThemeIcon,
  Divider,
} from "@mantine/core";
import { IconGavel, IconCircleCheck } from "@tabler/icons-react";
import { useTranslation } from "../context/TranslationContext";

export default function Legal() {
  const { t } = useTranslation();

  // Helper to render legal sections consistently
  const LegalSection = ({ title, content, listItems = [] }) => (
    <Box mb={rem(60)}>
      <Title order={2} size={rem(24)} fw={800} mb="md" c="blue.9">
        {title}
      </Title>
      {content && (
        <Text size="lg" lh={1.7} c="gray.8">
          {content}
        </Text>
      )}
      {listItems.length > 0 && (
        <List
          mt="md"
          spacing="sm"
          size="lg"
          center
          icon={
            <ThemeIcon color="blue" size={24} radius="xl" aria-hidden="true">
              <IconCircleCheck size={rem(16)} />
            </ThemeIcon>
          }
        >
          {listItems.map((item, index) => (
            <List.Item key={index}>{item}</List.Item>
          ))}
        </List>
      )}
    </Box>
  );

  return (
    <Box pb={rem(120)}>
      {/* 1. HEADER SECTION */}
      <Container size="lg" pt={rem(80)} pb={rem(60)}>
        <Stack gap="xs" align="center">
          <Badge variant="dot" color="blue" size="lg">
            {t("legalBadge", "Policy")}
          </Badge>
          <Title
            order={1}
            size={rem(48)}
            ta="center"
            fw={900}
            style={{ letterSpacing: rem(-1) }}
          >
            {t("legalTitle", "Terms and Conditions")}
          </Title>
          <Paper withBorder p="xl" radius="md" bg="blue.0" mt="xl" maw={800}>
            <Stack gap="md">
              <Text size="lg" fw={500} c="blue.9">
                {t("legalIntro")}
              </Text>
              <Text size="sm" c="dimmed">
                {t("legalContact")}
              </Text>
            </Stack>
          </Paper>
        </Stack>
      </Container>

      <Container size="lg">
        <Grid gutter={rem(80)}>
          {/* LEFT COLUMN: NAVIGATION HINT/ICON */}
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Box style={{ position: "sticky", top: rem(100) }}>
              <ThemeIcon
                size={60}
                radius="xl"
                color="blue"
                variant="light"
                mb="md"
                aria-hidden="true"
              >
                <IconGavel size={30} />
              </ThemeIcon>
              <Text fw={700} size="xl" mb="xs">
                INDUB AG
              </Text>
              <Text c="dimmed" size="sm">
                Yogi Foods
              </Text>
              <Text c="dimmed" size="sm">
                {t("location")}
              </Text>

              <Divider my="xl" />

              {/* <Text size="xs" c="dimmed" tt="uppercase" fw={700} lts={1}>
                {t("lastUpdated", "Last Updated: 2026")}
              </Text> */}
            </Box>
          </Grid.Col>

          {/* RIGHT COLUMN: MAIN LEGAL CONTENT */}
          <Grid.Col span={{ base: 12, md: 9 }}>
            <Stack gap={0}>
              <LegalSection
                title={t("termsOverview")}
                content={t("termsOverviewText")}
              />

              <LegalSection
                title={t("websiteChanges")}
                content={t("websiteChangesText")}
              />

              <LegalSection
                title={t("contractFormation")}
                content={t("contractFormationText")}
              />

              <LegalSection
                title={t("customerGuarantees")}
                content={t("customerGuaranteesIntro")}
                listItems={[t("guarantee1"), t("guarantee2"), t("guarantee3")]}
              />

              <LegalSection
                title={t("placingOrder")}
                content={t("placingOrderText")}
              />

              <LegalSection
                title={t("cancelOrder")}
                content={t("cancelOrderText")}
              />

              <LegalSection
                title={t("orderAcceptance")}
                content={t("orderAcceptanceText")}
              />

              <LegalSection title={t("delivery")} content={t("deliveryText")} />

              <LegalSection
                title={t("returnRight")}
                content={t("returnRightText")}
              />

              <LegalSection
                title={t("liability")}
                content={`${t("liabilityText1")} ${t("liabilityText2")}`}
              />

              <LegalSection
                title={t("severability")}
                content={t("severabilityText")}
              />
              <LegalSection
                title={t("jurisdiction")}
                content={t("jurisdictionText")}
              />
            </Stack>

            {/* FINAL SIGNATURE BOX */}
            <Paper withBorder p="xl" radius="lg" mt={rem(40)} bg="gray.0">
              <Stack gap={5} align="center">
                <Text fw={900} size={rem(20)}>
                  INDUB AG
                </Text>
                <Text size="md" c="gray.7">
                  Yogi Foods
                </Text>
                <Text size="md" c="gray.7">
                  {t("location")}
                </Text>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Container>
    </Box>
  );
}
